import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { Button, Field, Icon, Select } from '@zagru/design-system';
import { getRuntimeConfig, useAuth, useLocale, type RoleProfile } from '@zagru/platform';
import { usePlatformInfo } from '@/api/platform';
import { isMockMode, mockSignIn } from '@/app/mockAuth';
import { allProfiles, landingPage } from '@/app/navigation/navConfig';
import { safeReturnTo } from '@/app/state/appStores';
import { useShellT } from '@/app/shell/i18n';
import { AuthFrame } from './AuthFrame';
import { signInFailureCopy } from './authCopy';
import { autoSignInRetry } from './autoSignIn';

/** Mock-mode role profiles, read as `auth.login.profile.<profile>`. */
const profileLabelKey = (profile: RoleProfile) => `auth.login.profile.${profile}`;

/** `https://idp.zagru.one/realms/zagru` → `idp.zagru.one/realms/zagru`. Anything unparseable is shown as it came. */
function identityProviderLabel(issuer: string): string {
  try {
    const url = new URL(issuer);
    return url.pathname === '/' ? url.host : `${url.host}${url.pathname.replace(/\/$/, '')}`;
  } catch {
    return issuer;
  }
}

/**
 * `/login`: the way into Zagru One.
 *
 * Credentials are entered on the identity provider — the Zagru One identity pages, built from this design system
 * (ADR-0042) — never here: that is where two-factor, passkeys, password reset and single sign-on live, and it leaves
 * an SSO session behind so a reload restores the session silently. So this page does not ask the person to click
 * "Sign in" first (owner, 2026-09-25): on a first visit, after a deep link, after signing out and after an expired
 * session it sends the browser straight to the username and password form and draws nothing but the background
 * meanwhile. After a failure it retries once, then explains the failure and waits (`autoSignIn.ts`).
 *
 * Nothing about a workspace is sent or shown before sign-in (owner, 2026-09-25: a customer's name on the sign-in page
 * is a leak) — only the product. The redirect carries the viewer's language.
 *
 * What the footer says about the installation comes from `GET /api/v1/platform/info`, the one endpoint served
 * anonymously; every value falls back to the runtime configuration and a failure is silent. Mock mode keeps the
 * page as a role picker.
 */
export function LoginPage() {
  const t = useShellT();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { status, error, login } = useAuth();
  const { locale } = useLocale();
  const mock = isMockMode();
  const config = getRuntimeConfig();
  const { data: info } = usePlatformInfo();
  const environment = info?.environment ?? config.environment ?? 'development';
  // L-14 (R7): a build without a version (dev: "unknown") shows none.
  const version = info?.version && info.version !== 'unknown' ? `${info.version}${info.buildCommit ? ` · ${info.buildCommit}` : ''}` : null;
  const issuer = info?.oidc.issuer || config.oidc.issuer;
  const [profile, setProfile] = useState<RoleProfile>('supervisor');
  const [busy, setBusy] = useState(false);
  const returnTo = params.get('returnTo');
  const signedOut = params.get('signedOut') === '1';
  const failure = status === 'error' && error ? signInFailureCopy(error, t) : null;

  const [startFailed, setStartFailed] = useState(false);
  // Decided once per failure: the automatic retry is spent the moment it starts.
  const [retryAfterFailure] = useState(() => autoSignInRetry.allowed());
  const autoStart = !mock && !!issuer && !startFailed && status !== 'authenticated' && (!failure || retryAfterFailure);

  const startSignIn = () => {
    setBusy(true);
    void login(safeReturnTo(returnTo, '/'), { uiLocales: locale }).catch(() => {
      setBusy(false);
      setStartFailed(true);
    });
  };

  // Straight to the username and password form: there is no reason to make someone click twice.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (!autoStart || autoStarted.current || status === 'idle' || status === 'authenticating') return;
    autoStarted.current = true;
    if (failure) autoSignInRetry.mark();
    startSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart, status]);

  if (status === 'authenticated') {
    return <Navigate to={safeReturnTo(returnTo, '/')} replace />;
  }

  if (autoStart) {
    return (
      <main className="min-h-dvh bg-surface-0" aria-busy="true">
        <p role="status" className="sr-only">
          {t('auth.redirecting')}
        </p>
      </main>
    );
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (mock) {
      mockSignIn(profile);
      navigate(safeReturnTo(returnTo, landingPage[profile]), { replace: true });
      return;
    }
    startSignIn();
  };

  const title = failure ? failure.title : signedOut ? t('auth.login.signedOutTitle') : t('auth.login.title');
  const description = failure
    ? failure.description
    : signedOut
      ? t('auth.login.signedOutDescription')
      : t('auth.login.description');

  return (
    <AuthFrame
      title={title}
      description={description}
      technical={failure ? (error ?? undefined) : undefined}
      footer={
        <dl className="m-0 flex flex-col gap-1 type-caption text-fg-subtle" aria-label={t('auth.login.deployment')}>
          <div className="flex justify-between gap-4">
            <dt>{t('shell.context.environment')}</dt>
            <dd className="m-0 truncate text-fg-muted">{environment}</dd>
          </div>
          {version ? (
            <div className="flex justify-between gap-4">
              <dt>{t('common.version')}</dt>
              <dd className="m-0 truncate text-fg-muted tnum">{version}</dd>
            </div>
          ) : null}
          <div className="flex justify-between gap-4">
            <dt>{t('auth.login.identityProvider')}</dt>
            <dd className="m-0 truncate text-fg-muted" title={issuer || undefined}>
              {issuer ? identityProviderLabel(issuer) : t('auth.login.notConfigured')}
            </dd>
          </div>
        </dl>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {mock ? (
          <Field label={t('auth.login.profile')} htmlFor="login-profile" hint={t('auth.login.profileHint')}>
            <Select
              id="login-profile"
              value={profile}
              onValueChange={(v) => setProfile(v as RoleProfile)}
              options={allProfiles.map((p) => ({ value: p, label: t(profileLabelKey(p)) }))}
              fullWidth
            />
          </Field>
        ) : null}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={busy || status === 'authenticating'}
          disabled={!mock && !issuer}
          iconEnd={<Icon name="arrow-right" size={16} />}
        >
          {failure ? t('auth.startAgain') : t('auth.signIn')}
        </Button>
      </form>
    </AuthFrame>
  );
}
